import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError, AxiosResponse } from "axios";
import { url } from "../components/model/url-endpoint";
import { logger } from "../utils/logger";

export interface FipsCode {
  fips: string;
  name: string;
}

export interface FipsCodesState {
  loading: boolean;
  fipsCodes: FipsCode[];
  error: string | null;
  zipCodeCache: { [zipCode: string]: any }; // Cache for zipcode -> full response object
}

const initialState: FipsCodesState = {
  loading: false,
  fipsCodes: [],
  error: null,
  zipCodeCache: {},
};

const fipsCodesSlice = createSlice({
  name: "fipsCodes",
  initialState,
  reducers: {
    fetchFipsCodesStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchFipsCodesSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;

      // Extract zipcode from the response
      const zipcode = action.payload.zipcode;

      // Extract FIPS codes from the response structure
      let fipsCodes: FipsCode[] = [];
      const countyfips: string[] = action.payload.countyfips || [];

      // Store in the cache (even if countyfips is empty)
      if (zipcode) {
        state.zipCodeCache[zipcode] = action.payload; // Store the full response object
        logger.debug(
          `Redux: Cached response for ZIP ${zipcode}:`, action.payload
        );
      }

      // Also store as FipsCode objects for components that use that format
      if (
        action.payload &&
        action.payload.success &&
        action.payload.countyfips
      ) {
        // Map the countyfips array to FipsCode objects
        fipsCodes = countyfips.map((fips: string) => ({
          fips,
          name: `FIPS: ${fips}`,
        }));
        logger.debug(
          `Redux: Extracted ${fipsCodes.length} FIPS codes for ZIP ${zipcode}`
        );
      } else {
        logger.debug(
          "Redux: Invalid response format or no FIPS codes in response",
          action.payload
        );
      }

      state.fipsCodes = fipsCodes;
      state.error = null;
    },
    fetchFipsCodesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const fetchFipsCodes = (zipcode: string) => {
  return (
    dispatch: Dispatch<any>,
    getState: () => { fipsCodes: FipsCodesState }
  ) => {
    // Skip API call if ZIP code is invalid (must be exactly 5 digits)
    const zipRegEx = /^\d{5}$/;
    if (!zipcode || !zipRegEx.test(zipcode)) {
      return Promise.resolve([]);
    }

    // hard code VA state
    const usState = 'VA';

    // Get just the first 5 digits (for API call and cache)
    const zipCode5 = zipcode.substring(0, 5);

    // Check cache first
    const state = getState();

    if (!state.fipsCodes) {
      logger.error("fipsCodes state is undefined in Redux store");
      return Promise.resolve([]);
    }

    if (!state.fipsCodes.zipCodeCache) {
      logger.error("zipCodeCache is undefined in fipsCodes state");
      return Promise.resolve([]);
    }

    // Now cache stores the full response object, not just countyfips array
    const cachedResponse = state.fipsCodes.zipCodeCache[zipCode5];

    if (cachedResponse) {
      logger.debug(
        `Using cached FIPS response for ZIP ${zipCode5}:`,
        cachedResponse
      );
      dispatch(fipsCodesActions.fetchFipsCodesSuccess(cachedResponse));
      setTimeout(() => {
        const postDispatchState = getState();
        logger.debug('Redux state after cache hit dispatch:', postDispatchState.fipsCodes);
      }, 0);
      return Promise.resolve(cachedResponse);
    }

    // Not in cache, make the API call
    dispatch(fipsCodesActions.fetchFipsCodesStart());

    return axios
      .put(url + "get_fips_codes", {
        zipcode: zipCode5,
        state: usState
      })
      .then((response: AxiosResponse) => {
        // Store the full response object in the cache via the reducer only
        dispatch(fipsCodesActions.fetchFipsCodesSuccess(response.data));
        return response.data;
      })
      .catch((error: AxiosError) => {
        const errorMessage = error.message || "Failed to fetch FIPS codes";
        dispatch(fipsCodesActions.fetchFipsCodesFailure(errorMessage));
        return [];
      });
  };
};

export default fipsCodesSlice;
export const fipsCodesActions = fipsCodesSlice.actions;
